import React from "react";
import { isBefore, startOfDay } from "date-fns";
import { Formik, Form, Field, FormikHelpers } from "formik";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectGroup,
  SelectItem,
} from "@/components/ui/select";
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client";
import * as z from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";

import { getServiceColor } from "@/helpers/utils";
import { CURRENCIES, Currency } from "@/env";
import { DatePicker } from "@/components/common/DatePicker";
import { setToEndOfDay, convertTimezoneMoment } from "@/helpers/date";
import Deliverables from "./Deliverables/Deliverables";
import { Deliverable } from "./Deliverables/DeliverableRow";
import { CURRENCY_CODES } from "@/env";
import { CurrencyType } from "../types";
import {
  ServicesSpecsQuery,
  ServicesSpecsQueryVariables,
} from "@/__generated__/graphql";

export type Service = ServicesSpecsQuery["services"][number];

const serviceFormSchema = z.object({
  deadline: z.coerce.date({ invalid_type_error: "Must enter a deadline" }),
  currency: z
    .string({ required_error: "Currency is required" })
    .refine((val) => CURRENCY_CODES.includes(val), {
      message: "Please select a valid currency",
    }),
  deliverables: z
    .array(
      z.object({
        formatId: z.string({ required_error: "Format is required" }),
        specId: z.string({ required_error: "Specification is required" }),
        custom: z.boolean().optional(),
        formatName: z.string().optional(),
        specName: z.string().optional(),
      }),
    )
    .min(1, "At least one deliverable is required"),
  comments: z.string().nullable().optional(),
  attachments: z.array(z.string()).nullable().optional(),
});

const multipleServicesFormSchema = z.record(z.string(), serviceFormSchema);

export interface FormValues {
  deadline: Date;
  currency?: CurrencyType;
  comments?: string;
  deliverables: Deliverable[];
  attachments: string[];
}

interface TaskSpecsFormProps extends FormValues {
  projectId: string;
  service: Service;
  showSpecsFieldOnly?: boolean;
  showCurrencyField?: boolean;
  timezone?: string;
  serviceId: string;
  setFieldValue: (
    field: string,
    value: string | Deliverable[] | Date | undefined,
  ) => void;
}

const TaskSpecsForm: React.FC<TaskSpecsFormProps> = ({
  projectId,
  showCurrencyField,
  service: { specs, id, name },
  serviceId,
  setFieldValue,
  deadline,
  currency,
  timezone,
}) => {
  return (
    <div className="relative flex w-full bg-primary">
      <div
        className="w-1 h-full absolute"
        style={{
          background: getServiceColor(name),
          borderRadius: "24px 0 0 24px",
        }}
      />
      <div
        className="absolute text-sm py-1 px-[20px] rounded-b-[12px] left-6 text-white"
        style={{
          background: getServiceColor(name),
        }}
      >
        {name}
      </div>
      <div className="flex flex-col m-6 mt-10 text-base gap-[20px]">
        <div className="flex items-center">
          <div className="w-50">Deadline Requested</div>
          <DatePicker
            date={deadline}
            setDate={(value) => {
              if (!value) return;
              const endOfDay = setToEndOfDay(value);
              const timezoneAwareDate = timezone
                ? convertTimezoneMoment(endOfDay, timezone)
                : endOfDay;
              setFieldValue(`${serviceId}.deadline`, timezoneAwareDate);
            }}
            placeholder="Enter Deadline"
            className="!p-3"
            triggerClassName="!border-none !bg-tertiary !w-50 !text-primary-foreground"
            disabled={(date) =>
              isBefore(startOfDay(date), startOfDay(new Date()))
            }
          />
        </div>
        <div className="flex items-center">
          <div className="w-50">Bid Currency</div>
          <Select
            value={currency}
            onValueChange={(value: CurrencyType) => {
              setFieldValue(`${serviceId}.currency`, value);
            }}
            disabled={!showCurrencyField}
          >
            <SelectTrigger className="w-50 border-none !bg-tertiary text-primary-foreground">
              {currency}
            </SelectTrigger>
            <SelectContent className="w-50">
              <SelectGroup>
                {CURRENCIES.map((curr: Currency) => (
                  <SelectItem key={curr.code} value={curr.code}>
                    {curr.label} {curr.code}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
        {specs && (
          <Field
            name={`${serviceId}.deliverables`}
            specOptions={specs}
            component={Deliverables}
            onChange={(deliverables: Deliverable[]) => {
              setFieldValue(`${serviceId}.deliverables`, deliverables);
            }}
            showEmptyDeliverableRow
            projectId={projectId}
            serviceId={id}
          />
        )}
      </div>
    </div>
  );
};

export const SERVICES_SPECS_QUERY = gql`
  query ServicesSpecs($ids: [SmartID!]!) {
    services(ids: $ids) {
      id
      name
      specs: deliverySpecs {
        __typename
        id
        name: specification
        formats {
          id
          format
        }
      }
    }
  }
`;

interface SelectSpecsProps {
  projectId: string;
  serviceIds: string[];
  showSpecsFieldOnly?: boolean;
  showCurrencyField?: boolean;
  timezone?: string;
  currency?: CurrencyType;
  onNext: (
    values: MultipleServicesFormValues,
    formikBag: FormikHelpers<MultipleServicesFormValues>,
  ) => void;
}

export interface MultipleServicesFormValues {
  [serviceId: string]: FormValues;
}

const SelectSpecs: React.FC<SelectSpecsProps> = ({
  serviceIds,
  onNext,
  currency = "USD",
  ...otherProps
}) => {
  const { data, loading, error } = useQuery<
    ServicesSpecsQuery,
    ServicesSpecsQueryVariables
  >(SERVICES_SPECS_QUERY, {
    fetchPolicy: "no-cache",
    variables: {
      ids: serviceIds,
    },
  });

  if (loading) return <div>Loading services...</div>;
  if (error) return <div>Error loading services: {error.toString()}</div>;
  if (!data?.services) return <div>Services not found</div>;

  const noSpecs = data.services.every((service) => !service.specs?.length);
  if (noSpecs) return <div>No delivery specs found</div>;

  const defaultValues: MultipleServicesFormValues = data.services.reduce(
    (acc, service) => ({
      ...acc,
      [service.id]: {
        deadline: undefined,
        currency,
        comments: "",
        deliverables: [],
        attachments: [],
      },
    }),
    {},
  );

  return (
    <div className="flex flex-col grow overflow-hidden h-full">
      <Formik<MultipleServicesFormValues>
        initialValues={defaultValues}
        validationSchema={toFormikValidationSchema(multipleServicesFormSchema)}
        validateOnChange={true}
        onSubmit={onNext}
      >
        {({ isSubmitting, isValid, setFieldValue, values }) => (
          <Form className="flex flex-col h-full">
            <div className="flex flex-col gap-4 overflow-scroll max-h-[calc(100%-180px)]">
              {data.services.map((service) => (
                <TaskSpecsForm
                  key={service.id}
                  service={service}
                  serviceId={service.id}
                  setFieldValue={setFieldValue}
                  showSpecsFieldOnly={otherProps.showSpecsFieldOnly}
                  showCurrencyField={otherProps.showCurrencyField}
                  timezone={otherProps.timezone}
                  projectId={otherProps.projectId}
                  {...values[service.id]}
                />
              ))}
            </div>
            <div className="mt-2 bg-primary py-[16px] px-[24px] flex justify-end rounded-b-[24px] h-[68px]">
              <Button
                type="submit"
                className="text-white py-[4px] px-[30px] rounded !border-white transition !bg-service-paint hover:opacity-80 disabled:opacity-50 "
                disabled={isSubmitting || !isValid}
              >
                Next
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default SelectSpecs;
